import { observer, inject } from 'mobx-react';
import { useTranslations } from 'next-intl';
import { getPriceDetails, renderPrice, renderTypePrice, renderWeightPrice, renderVariationPrice, formatPrice } from '@/utils/misc';
import { getBulkSaleRule, calculateSetPrice } from '@/utils/bulkDiscounts';

const Price = inject('wooCommerceStore')(observer(({
  product,
  productType,
  setProductDisplayType,
  wooCommerceStore,
}) => {
  const { regular_price: regularPrice, weight, weightable: productWeightOption, units_based: productUnitsOption, pack_based: productPackOption } = product;
  const weightUnit = wooCommerceStore.weightUnit || 'kg'; // Default to 'kg' if no setting available
  const tu = useTranslations('Units');
  const t = useTranslations('Product');

  const renderBulkSaleInfo = () => {
    const rule = getBulkSaleRule(product);
    if (!rule) return null;

    const { buy_quantity, get_quantity } = rule;
    const totalQuantity = buy_quantity + get_quantity;

    // Calculate total price based on discount rules
    const totalPrice = calculateSetPrice(rule, regularPrice);

    return (
      <div className="text-sm text-secondary-studio mt-1">
        {t('buy_x_for_y', { x: totalQuantity, y: formatPrice(totalPrice) })}
      </div>
    );
  };

  if (productType === 'simple') {
    const priceDetails = getPriceDetails(product, weightUnit, tu);
    setProductDisplayType(priceDetails.perUnit);
    if (productWeightOption && weight > 0 && regularPrice > 0) {
      return (
        <>
          {renderWeightPrice(priceDetails)}
          {renderBulkSaleInfo()}
        </>
      );
    } else if (!productWeightOption && (productUnitsOption || productPackOption)) {
      setProductDisplayType(priceDetails.perUnitDisplay);
      return (
        <>
          {renderTypePrice(priceDetails)}
          {renderBulkSaleInfo()}
        </>
      );
    } else {
      setProductDisplayType(priceDetails.perUnitDisplay);
      return (
        <>
          {renderPrice(priceDetails)}
          {renderBulkSaleInfo()}
        </>
      );
    }
  } else if (productType === 'variable' && product) {
    // Handle the pricing display for the selected variation.
    const variationDetails = getPriceDetails(product, weightUnit, tu, productType);
    setProductDisplayType(variationDetails.perUnit);
    if (productWeightOption && weight > 0 && regularPrice > 0) {
      return (
        <>
          {renderWeightPrice(variationDetails)}
          {renderBulkSaleInfo()}
        </>
      );
    } else {
      setProductDisplayType(variationDetails.perUnitDisplay);
      return (
        <>
          {renderVariationPrice(variationDetails)}
          {renderBulkSaleInfo()}
        </>
      );
    }
  }

  return null;
}));

export default Price;
